// Client preview for Part uses CSV/JSON bulk import.

import { parseBulkImport } from "./bulkImportParser";
import type { MfgMaterial, MfgMaterialUse, MfgMaterialUseResolveMode } from "./types";

export type MaterialUseImportMember = {
  material_code: string;
  material_id: string | null;
  qty_per_use: number;
  preference_rank: number;
  is_active: boolean;
  is_primary: boolean;
  finish_code: string | null;
  unresolved: boolean;
};

export type MaterialUseImportRow = {
  use_code: string;
  use_name: string;
  unit: string;
  notes: string;
  is_active: boolean;
  resolve_mode: MfgMaterialUseResolveMode;
  finish_channel: string | null;
  members: MaterialUseImportMember[];
  exists: boolean;
  blocked: boolean;
  blockReason: string | null;
};

export type MaterialUsesBulkImportPreview =
  | { kind: "empty" }
  | { kind: "error"; error: string }
  | {
      kind: "ok";
      uses: MaterialUseImportRow[];
      totals: { create: number; update: number; blocked: number; unresolvedMembers: number };
    };

function cell(row: Record<string, unknown>, key: string): string {
  const v = row[key];
  if (v === null || v === undefined) return "";
  return String(v).trim();
}

function parseBool(raw: string, defaultValue: boolean): boolean {
  if (!raw) return defaultValue;
  const s = raw.toLowerCase();
  if (s === "true" || s === "1" || s === "yes") return true;
  if (s === "false" || s === "0" || s === "no") return false;
  return defaultValue;
}

function parseNumber(raw: string, fallback: number): number {
  if (!raw) return fallback;
  const n = Number(raw);
  return Number.isFinite(n) ? n : fallback;
}

export function buildMaterialUsesBulkImportPreview(
  text: string,
  existingUses: ReadonlyArray<MfgMaterialUse>,
  materials: ReadonlyArray<MfgMaterial>,
): MaterialUsesBulkImportPreview {
  const trimmed = text.trim();
  if (!trimmed) return { kind: "empty" };

  const parsed = parseBulkImport(trimmed);
  if (!parsed.ok) return { kind: "error", error: parsed.error };

  const byCode = new Map(existingUses.map((u) => [u.use_code.trim().toLowerCase(), u]));
  const matByCode = new Map(
    materials.map((m) => [m.code.trim().toLowerCase(), m] as const).filter(([c]) => Boolean(c)),
  );

  const grouped = new Map<string, MaterialUseImportRow>();

  for (const raw of parsed.rows) {
    const use_code = cell(raw, "use_code");
    const use_name = cell(raw, "use_name") || cell(raw, "name");
    if (!use_code) {
      return { kind: "error", error: "Each row needs use_code." };
    }

    const resolveRaw = cell(raw, "resolve_mode").toLowerCase();
    const resolve_mode: MfgMaterialUseResolveMode =
      resolveRaw === "finish_variant" ? "finish_variant" : "substitute";
    const finish_channel = cell(raw, "finish_channel") || null;

    let row = grouped.get(use_code.toLowerCase());
    if (!row) {
      row = {
        use_code,
        use_name: use_name || use_code,
        unit: cell(raw, "unit") || "each",
        notes: cell(raw, "notes"),
        is_active: parseBool(cell(raw, "is_active"), true),
        resolve_mode,
        finish_channel: resolve_mode === "finish_variant" ? finish_channel : null,
        members: [],
        exists: byCode.has(use_code.toLowerCase()),
        blocked: false,
        blockReason: null,
      };
      grouped.set(use_code.toLowerCase(), row);
    } else {
      if (use_name) row.use_name = use_name;
      if (cell(raw, "unit")) row.unit = cell(raw, "unit");
      if (cell(raw, "notes")) row.notes = cell(raw, "notes");
      if (cell(raw, "is_active")) row.is_active = parseBool(cell(raw, "is_active"), row.is_active);
      if (resolveRaw) {
        row.resolve_mode = resolve_mode;
        row.finish_channel = resolve_mode === "finish_variant" ? finish_channel : null;
      }
    }

    const memberCode = cell(raw, "member_material_code");
    if (!memberCode) continue;

    const mat = matByCode.get(memberCode.toLowerCase()) ?? null;
    row.members.push({
      material_code: memberCode,
      material_id: mat?.id ?? null,
      qty_per_use: parseNumber(cell(raw, "member_qty_per_use"), 1),
      preference_rank: parseNumber(cell(raw, "preference_rank"), 100),
      is_active: parseBool(cell(raw, "member_is_active"), true),
      is_primary: parseBool(cell(raw, "is_primary"), false),
      finish_code: cell(raw, "finish_code") || null,
      unresolved: !mat,
    });
  }

  const uses = [...grouped.values()];
  for (const u of uses) {
    if (u.resolve_mode === "finish_variant" && !u.finish_channel) {
      u.blocked = true;
      u.blockReason = "finish_channel is required for finish_variant uses.";
    }
    const unresolved = u.members.filter((m) => m.unresolved);
    if (unresolved.length > 0) {
      u.blocked = true;
      u.blockReason = `Unknown material code(s): ${unresolved
        .map((m) => m.material_code)
        .join(", ")}`;
    }
  }

  return {
    kind: "ok",
    uses,
    totals: {
      create: uses.filter((u) => !u.exists && !u.blocked).length,
      update: uses.filter((u) => u.exists && !u.blocked).length,
      blocked: uses.filter((u) => u.blocked).length,
      unresolvedMembers: uses.reduce(
        (n, u) => n + u.members.filter((m) => m.unresolved).length,
        0,
      ),
    },
  };
}
